import {
  useReducer,
  useEffect,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react'
import { CartContext, type CartItem } from './CartContext'

interface CartProviderProps {
  children: ReactNode
}

// Cart state type
interface CartState {
  items: CartItem[]
  isOpen: boolean
  lastUpdated: string
}

// Cart action types
type CartAction =
  | { type: 'ADD_ITEM'; payload: CartItem }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_IS_OPEN'; payload: boolean }
  | { type: 'TOGGLE_CART' }
  | { type: 'LOAD_FROM_STORAGE'; payload: CartState }
  | { type: 'SYNC_TO_STORAGE' }

// Cart reducer
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItemIndex = state.items.findIndex(
        item => item.id === action.payload.id
      )

      if (existingItemIndex > -1) {
        // Update existing item quantity
        const updatedItems = [...state.items]
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity:
            updatedItems[existingItemIndex].quantity + action.payload.quantity,
        }

        return {
          ...state,
          items: updatedItems,
          lastUpdated: new Date().toISOString(),
        }
      } else {
        // Add new item
        return {
          ...state,
          items: [...state.items, action.payload],
          lastUpdated: new Date().toISOString(),
        }
      }
    }

    case 'REMOVE_ITEM': {
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload),
        lastUpdated: new Date().toISOString(),
      }
    }

    case 'UPDATE_QUANTITY': {
      const { itemId, quantity } = action.payload

      if (quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(item => item.id !== itemId),
          lastUpdated: new Date().toISOString(),
        }
      }

      return {
        ...state,
        items: state.items.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        ),
        lastUpdated: new Date().toISOString(),
      }
    }

    case 'CLEAR_CART': {
      return {
        ...state,
        items: [],
        lastUpdated: new Date().toISOString(),
      }
    }

    case 'SET_IS_OPEN': {
      return {
        ...state,
        isOpen: action.payload,
      }
    }

    case 'TOGGLE_CART': {
      return {
        ...state,
        isOpen: !state.isOpen,
      }
    }

    case 'LOAD_FROM_STORAGE': {
      return {
        ...action.payload,
        lastUpdated: new Date().toISOString(),
      }
    }

    case 'SYNC_TO_STORAGE': {
      return state
    }

    default:
      return state
  }
}

// Storage utilities
const CART_STORAGE_KEY = 'wunschparadies_cart'

const loadCartFromStorage = (): CartState => {
  try {
    if (typeof window === 'undefined') return getInitialState()

    const stored = localStorage.getItem(CART_STORAGE_KEY)
    if (!stored) return getInitialState()

    const parsed = JSON.parse(stored)

    // Validate stored data
    if (!parsed.items || !Array.isArray(parsed.items)) {
      return getInitialState()
    }

    // Clean up old data (older than 30 days)
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

    if (parsed.lastUpdated && new Date(parsed.lastUpdated) < thirtyDaysAgo) {
      localStorage.removeItem(CART_STORAGE_KEY)
      return getInitialState()
    }

    return {
      items: parsed.items || [],
      isOpen: parsed.isOpen || false,
      lastUpdated: parsed.lastUpdated || new Date().toISOString(),
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load cart from storage:', error)
    return getInitialState()
  }
}

const saveCartToStorage = (state: CartState): void => {
  try {
    if (typeof window === 'undefined') return

    localStorage.setItem(
      CART_STORAGE_KEY,
      JSON.stringify({
        items: state.items,
        isOpen: state.isOpen,
        lastUpdated: state.lastUpdated,
      })
    )
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to save cart to storage:', error)
  }
}

const getInitialState = (): CartState => ({
  items: [],
  isOpen: false,
  lastUpdated: new Date().toISOString(),
})

export const CartProvider = ({ children }: CartProviderProps) => {
  const [state, dispatch] = useReducer(cartReducer, getInitialState())

  // Load cart from storage on mount
  useEffect(() => {
    const savedCart = loadCartFromStorage()
    dispatch({ type: 'LOAD_FROM_STORAGE', payload: savedCart })
  }, [])

  // Save cart to storage when state changes
  useEffect(() => {
    if (
      state.items.length > 0 ||
      state.lastUpdated !== getInitialState().lastUpdated
    ) {
      saveCartToStorage(state)
    }
  }, [state])

  // Calculate derived values
  const totalItems = useMemo(
    () => state.items.reduce((total, item) => total + item.quantity, 0),
    [state.items]
  )

  const totalPrice = useMemo(
    () =>
      state.items.reduce(
        (total, item) => total + item.price * item.quantity,
        0
      ),
    [state.items]
  )

  // Memoized actions
  const addItem = useCallback((item: CartItem) => {
    dispatch({ type: 'ADD_ITEM', payload: item })
  }, [])

  const removeItem = useCallback((itemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: itemId })
  }, [])

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { itemId, quantity } })
  }, [])

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' })
  }, [])

  const setIsOpen = useCallback((isOpen: boolean) => {
    dispatch({ type: 'SET_IS_OPEN', payload: isOpen })
  }, [])

  const toggleCart = useCallback(() => {
    dispatch({ type: 'TOGGLE_CART' })
  }, [])

  // Context value
  const contextValue = useMemo(
    () => ({
      ...state,
      totalItems,
      totalPrice,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      setIsOpen,
      toggleCart,
    }),
    [
      state,
      totalItems,
      totalPrice,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      setIsOpen,
      toggleCart,
    ]
  )

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  )
}
